import { PointerEvent, useCallback } from 'react';
import styled from 'styled-components';

export const rhythmTapButtonId = 'rhythm-tap-button';
export const rhythmSpaceBarHint = 'Space bar works too';

// The big round tap button from the "Rhythm Echo (listen)" artboard (issue #3473): 220 px, accent
// filled, "TAP" with a caption underneath ("on Go!", "now", "with each click"). It
// taps on pointerdown rather than click, which on a phone is tens of ms sooner and is the moment
// the singer means; `onTap` gets the event's timeStamp so the audio session can stamp it. `open`
// is the button's look only: it styles the button, picks nothing, and marks it for stories. The
// page gates taps itself and plays the tap sound only for taps it keeps. In a pattern the look
// follows Go! (isTapButtonOpen) while the page keeps taps from Set, a beat earlier (isTapOpen), so
// a keen singer's early tap counts though the button still reads "on Go!"; in the warm-up the look
// and the gate are the same.
export const RhythmTapButton = ({
  open,
  caption,
  onTap,
}: {
  open: boolean;
  caption: string;
  onTap: (perfMs: number) => void;
}) => {
  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      event.preventDefault(); // no focus ring, no text selection, no mouse compatibility events
      onTap(event.timeStamp);
    },
    [onTap],
  );
  return (
    <Button
      id={rhythmTapButtonId}
      data-testid={rhythmTapButtonId}
      data-open={open ? 'true' : 'false'}
      $open={open}
      onPointerDown={onPointerDown}
      onContextMenu={(event) => event.preventDefault()}
      aria-label="Tap"
    >
      <Label>TAP</Label>
      <Caption>{caption}</Caption>
    </Button>
  );
};

export const SpaceBarHint = styled.div`
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: var(--color-text-darker);
`;

export const rhythmTapButtonSize = 220;
const shortScreen = '@media (max-height: 700px)';

const Button = styled.button<{ $open: boolean }>`
  flex: none; /* a short screen scrolls the body rather than squashing the circle into an ellipse (#3559) */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: ${rhythmTapButtonSize}px;
  height: ${rhythmTapButtonSize}px;
  margin: 0 auto;
  border: none;
  border-radius: 50%;
  background-color: var(--color-tab-selected);
  color: var(--color-background);
  opacity: ${({ $open }) => ($open ? 1 : 0.7)};
  cursor: pointer;
  touch-action: none; /* no double-tap zoom, no scroll from a tap */
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 120ms linear;

  &:active {
    filter: brightness(0.9);
  }

  ${shortScreen} {
    width: 180px;
    height: 180px;
  }
`;

const Label = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 28px;
  line-height: 1.2;
  letter-spacing: 0.04em;
`;

const Caption = styled.div`
  font-size: 14px;
  line-height: 1.3;
`;
